import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ContentEditor from '../../ContentEditor';
import HeaderToolbar from './HeaderToolbar';
import NodeActivityEditor from '../../NodeActivityEditor';
import SecondaryButton from '../../SecondaryButton';
import PrimaryButton from '../../PrimaryButton';
import { ViewSection, AutoSaveStatus, LockInfo } from '../styles';
import { Questions, Question, SubmitPanel } from '../../NodeContentPreview/styles';

const WrittenQuizContentViewer = ({
  type,
  report,
  isEditable,
  saveReport,
  data,
  fileService,
  autoSaveStatus,
  integrations,
  isTeacher,
}) => {
  const isLocked = report?.locked;

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [showToolbar, setShowToolbar] = useState({});

  const mapAnswers = (answers) => {
    const newAnswers = {};
    if (!isTeacher) {
      answers.forEach((answer) => {
        newAnswers[`_${answer.question_id}`] = { body: answer.text, uuid: answer.uuid, isDraft: answer.draft };
      });
    }
    return newAnswers;
  };

  useEffect(() => {
    setQuestions(data.QuizQuestions || []);
    const questionToolbarToggles = data.QuizQuestions.reduce((acc, curr) => ((acc[curr.id] = false), acc), {});
    setShowToolbar(questionToolbarToggles);
    if (report) {
      setAnswers(mapAnswers(report.QuizAnswers || []));
    }
  }, []);

  const onChangeReport = (value, question) => {
    const { id } = question;
    const newAnswers = { [`_${id}`]: { body: value, isDraft: true, uuid: getAnswerId(question) } };
    setAnswers(newAnswers);
    saveReport({ ...report, answers: newAnswers, editorId: `question_${id}` });
  };

  const revokeReport = () => {
    saveReport({ ...report, state: 'active' });
  };

  const submitReport = () => {
    setAnswers(mapAnswers(report.QuizAnswers || []));
    saveReport({ ...report, state: 'done' });
  };

  const toggleToolbar = (id) => {
    setShowToolbar({ ...showToolbar, [id]: !showToolbar[id] });
  };

  const toggleComments = (answerId) => {
    document.dispatchEvent(
      new CustomEvent('toggleComments', { detail: { entityId: answerId, entityType: 'quiz_answer' } }),
    );
  };

  const getAnswer = (question) => {
    if (answers[`_${question.id}`]) {
      return answers[`_${question.id}`];
    } else {
      return {};
    }
  };

  const getAnswerId = (question) => {
    const answer = getAnswer(question);
    return answer.uuid;
  };

  const getAnswerText = (question) => {
    const answer = getAnswer(question);
    return answer.body || '';
  };

  const placeholderKey = isTeacher ? 'written_quiz_student_input_placeholder' : 'task_texteditor_placeholder';

  const getViewForQuestion = (question, index) => {
    const editorPanel = (
      <ContentEditor
        id={`question_${question.id}`}
        type={type}
        aria-label={i18n.__(placeholderKey)}
        placeholder={i18n.__(placeholderKey)}
        value={getAnswerText(question)}
        onChange={(value) => isEditable && onChangeReport(value, question)}
        integrations={integrations}
        fileService={fileService}
        disabled={!isEditable}
        showToolbar={showToolbar[question.id]}
        className="question"
        memoize={true}
      />
    );

    const toggleQuestionComments = () => toggleComments(getAnswerId(question));
    const setQuestionToolbar = () => toggleToolbar(question.id);

    const headerRightPanel = (
      <HeaderToolbar
        isEditable={isEditable}
        report={report}
        isTeacher={isTeacher}
        editorId={`question_${question.id}`}
        showToolbar={showToolbar[question.id]}
        setShowToolbar={setQuestionToolbar}
        toggleComments={toggleQuestionComments}
      />
    );

    const buttonPanel = isTeacher ? null : <AutoSaveStatus>{autoSaveStatus[`question_${question.id}`]}</AutoSaveStatus>;

    return (
      <NodeActivityEditor
        type={type}
        index={index}
        headerText={question.body}
        headerRightPanel={headerRightPanel}
        editorPanel={editorPanel}
        buttonPanel={buttonPanel}
        className="question"
      />
    );
  };

  const mappedQuestions = questions.map((question, index) => (
    <Question key={question.id} className={'ir-content'}>
      {getViewForQuestion(question, index)}
    </Question>
  ));

  return (
    <>
      <ViewSection>
        <Questions>{mappedQuestions}</Questions>
      </ViewSection>
      {!isTeacher && (
        <SubmitPanel className={'ir-hide'}>
          {isLocked ? (
            <LockInfo>{i18n.__('editor_locked_desc')}</LockInfo>
          ) : isEditable ? (
            <SecondaryButton text={i18n.__('app_handin')} onClick={submitReport} disabled={isTeacher} />
          ) : (
            <PrimaryButton text={i18n.__('app_edit')} onClick={revokeReport} disabled={isTeacher} />
          )}
        </SubmitPanel>
      )}
    </>
  );
};

export default WrittenQuizContentViewer;

WrittenQuizContentViewer.propTypes = {
  type: PropTypes.string.isRequired,
  report: PropTypes.object,
  isEditable: PropTypes.bool.isRequired,
  saveReport: PropTypes.func.isRequired,
  data: PropTypes.object,
  fileService: PropTypes.object.isRequired,
  integrations: PropTypes.object.isRequired,
  autoSaveStatus: PropTypes.object.isRequired,
  isTeacher: PropTypes.bool.isRequired,
};
